'use client';
import styles from './headerwcp.module.sass';
import cn from 'classnames';
import { MainMenu, MainMenuProps } from '../MainMenu';
import { SiteLogo, ISiteLogoProps } from '@intigral/shared/components/SiteLogo';
import { useMediaQuery } from '@intigral/hooks/dom/useMediaQuery/useMediaQuery';
import { Avatar, FontIcon } from '@intigral/shared';
import { FontIconName } from '@intigral/shared/components/FontIcon';
import { LinkProps, LinkWrapper } from '@intigral/shared/components/LinkWrapper';
import React, { useEffect, useState, forwardRef } from 'react';

/** Figma "Profile Menu" */
export interface HeaderWcpMobileMenu {
    mySpace?: LinkProps & { name: string; image?: string };
    /** Other profiles listed above the account links (replaces mySpace when set) */
    profiles?: React.ReactNode;
    items?: MainMenuProps['nav'];
    signOut?: { text: string; onClick: (event: React.MouseEvent) => void };
    social?: { href: string; icon: FontIconName; label?: string; target?: string }[];
}

export interface HeaderWCPProps extends React.HTMLAttributes<HTMLDivElement> {
    logoProps?: ISiteLogoProps;
    menuProps?: MainMenuProps | React.ReactElement;
    children?: React.ReactElement | React.ReactElement[];
    staticChildren?: React.ReactElement | React.ReactElement[];
    mobileMenu?: HeaderWcpMobileMenu;
}

export const HeaderWcp = forwardRef<HTMLDivElement, HeaderWCPProps>(
    ({ menuProps, logoProps, children, staticChildren, mobileMenu, ...props }, ref) => {
        const [isNavVisible, setIsNavVisible] = useState(false);

        // const toggleNavVisibility = () => setIsNavVisible((prev) => !prev);

        const isMobile = useMediaQuery('(max-width: 924px)');
        const [isSticky, setIsSticky] = useState(false);

        const toggleNavVisibility = () => {
            setIsNavVisible((prev) => {
                const newVisibility = !prev;

                if (newVisibility) {
                    document.body.classList.add('nav-visible');
                } else {
                    document.body.classList.remove('nav-visible');
                }
                return newVisibility;
            });
        };

        const handleMenuItemClick = (event: React.MouseEvent) => {
            const target = event.target as HTMLElement;

            if (target.closest(`.${styles.expandIcon}`)) {
                return; // Do nothing if a dropdown toggle is clicked
            }

            setIsNavVisible(false);
            document.body.classList.remove('nav-visible');
        };

        useEffect(() => {
            const thresholdPx = isMobile ? 100 : 300; // scroll distance in pixels before header becomes sticky
            // Desktop .sticky shrinks in-flow height by ~48px (padding 5×step→1×step). That drop
            // can pull scrollY back across a narrow band and toggle sticky in a loop. Buffer must
            // exceed that delta; mobile keeps a smaller band (no padding shrink).
            const buffer = isMobile ? 20 : 60;

            const handleScroll = () => {
                const currentScrollY = window.scrollY;

                if (currentScrollY === 0) {
                    setIsSticky(false);
                    return;
                }

                setIsSticky((prev) => {
                    if (!prev && currentScrollY > thresholdPx + buffer) return true;
                    if (prev && currentScrollY <= thresholdPx - buffer) return false;
                    return prev;
                });
            };

            handleScroll();

            window.addEventListener('scroll', handleScroll, { passive: true });
            return () => {
                window.removeEventListener('scroll', handleScroll);
            };
        }, [isNavVisible, isMobile]);

        return (
            <div
                ref={ref}
                className={cn(
                    styles.header,
                    props.className,
                    isSticky && styles.sticky,
                    isNavVisible && styles.navVisible,
                    !menuProps && styles.gridtwo
                )}
                {...props}
            >
                {logoProps && <SiteLogo {...logoProps} className={styles.logo} />}
                {!isMobile && (
                    <>
                        {/* {menuProps ? <MainMenu {...menuProps} /> : <div></div>} */}
                        {menuProps && React.isValidElement(menuProps) ? (
                            menuProps
                        ) : menuProps ? (
                            <MainMenu {...(menuProps as MainMenuProps)} />
                        ) : null}
                        <div className={styles.right}>
                            {children && children}
                            {staticChildren && staticChildren}
                        </div>
                    </>
                )}

                {isMobile && (
                    <>
                        <div className={cn(styles.right, isNavVisible && styles.navshown)}>
                            {staticChildren && staticChildren}
                            {menuProps && (
                                <div onClick={toggleNavVisibility} className={styles.bars}>
                                    {isNavVisible ? (
                                        <FontIcon name="close-lg" size="smaller" md="smaller" />
                                    ) : (
                                        <>
                                            <FontIcon
                                                name="menu-left"
                                                size="smaller"
                                                md="smaller"
                                                className={styles.ltr}
                                            />
                                            <FontIcon
                                                name="menu-right"
                                                size="smaller"
                                                md="smaller"
                                                className={styles.rtl}
                                            />
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className={cn(styles.frame, isNavVisible && styles.visible)}>
                            {children && children}
                            {menuProps && !React.isValidElement(menuProps) && (
                                <MainMenu
                                    {...menuProps}
                                    onClick={menuProps.onClick ? menuProps.onClick : handleMenuItemClick}
                                    onDropdownItemClick={() => {
                                        setIsNavVisible(false); // Close the menu
                                        // document.body.classList.remove('nav-visible'); // Remove any menu visibility classes
                                    }}
                                />
                            )}
                            {mobileMenu && (
                                <div
                                    className={styles.bottom}
                                    onClick={(event) => {
                                        if ((event.target as HTMLElement).closest('a')) {
                                            setIsNavVisible(false);
                                            document.body.classList.remove('nav-visible');
                                        }
                                    }}
                                >
                                    {mobileMenu.profiles}
                                    {!mobileMenu.profiles && mobileMenu.mySpace && (
                                        <LinkWrapper
                                            link={mobileMenu.mySpace}
                                            className={styles.mySpace}
                                            data-test-id="profile-menu-my-space"
                                        >
                                            <Avatar
                                                border
                                                color="rgb(165, 78, 225)"
                                                image={mobileMenu.mySpace.image}
                                                name={mobileMenu.mySpace.name}
                                                variant="header"
                                                width="6x"
                                            />
                                        </LinkWrapper>
                                    )}
                                    {!!mobileMenu.items?.length && (
                                        <MainMenu className={styles.account} nav={mobileMenu.items} />
                                    )}
                                    {mobileMenu.signOut && (
                                        <MainMenu
                                            nav={[{ href: '', text: mobileMenu.signOut.text, icon: 'logout' }]}
                                            onClick={mobileMenu.signOut.onClick}
                                            data-test-id="profile-menu-logout"
                                        />
                                    )}
                                    {!!mobileMenu.social?.length && (
                                        <div className={styles.social}>
                                            {mobileMenu.social.map((link) => (
                                                <a
                                                    key={link.icon}
                                                    href={link.href}
                                                    target={link.target || '_blank'}
                                                    rel="noreferrer"
                                                    aria-label={link.label}
                                                >
                                                    <FontIcon name={link.icon} size="smaller" md="smaller" />
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}
                {/* </div> */}
            </div>
        );
    }
);
